import React, { useEffect, useState } from "react";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import { Link } from "react-router-dom";

// ================= TYPES =================
interface CampaignItem {
  id: number;
  title: string;
  slug: string;
  image: string;
  target_donation: string;
  total_donations: string;
  category?: {
    id: number;
    name: string;
    slug: string;
  } | null;
}

interface ApiResponse {
  current_page: number;
  data: CampaignItem[];
}

// ================= HELPERS =================
const formatRupiah = (num: number): string => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(num || 0);
};

const ProgressBar: React.FC<{ value: number; target: number }> = ({ value, target }) => {
  const percent = target ? Math.min((value / target) * 100, 100) : 0;

  return (
    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
      <div className="h-full bg-primary2" style={{ width: `${percent}%` }} />
    </div>
  );
};

const CampaignCard: React.FC<{ item: CampaignItem }> = ({ item }) => {
  const target = Number(item.target_donation);
  const collected = Number(item.total_donations);

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden">
      <div className="relative">
        <Link to={`/donasi/${item.slug}`}>
          <img src={`${item.image}`} alt={item.title} className="w-full h-44 object-cover" />
        </Link>
        <div className="absolute top-3 right-3 bg-primary2 text-white text-xs px-3 py-1 rounded-full">{item?.category?.name || "Kemanusiaan"}</div>
      </div>

      <div className="p-4 space-y-3">
        <Link to={`/donasi/${item.slug}`}>
          <h3 className="font-semibold text-gray-800 line-clamp-2 min-h-[48px]">{item.title}</h3>
        </Link>

        <ProgressBar value={collected} target={target} />

        <div className="flex justify-between text-sm">
          <div>
            <div className="text-gray-400">Terkumpul</div>
            <div className="font-semibold text-primary2">{formatRupiah(collected)}</div>
          </div>
          <div className="text-right">
            <div className="text-gray-400">Target</div>
            <div className="font-semibold text-gray-700">{formatRupiah(target)}</div>
          </div>
        </div>

        <Link to={`/donasi/${item.slug}`}>
          <button className="w-full bg-primary2 hover:bg-primary2 text-white py-2 rounded-xl font-medium mt-3">Patungan Sekarang</button>
        </Link>
      </div>
    </div>
  );
};

const SedangMendesakCarousel: React.FC = () => {
  const [data, setData] = useState<CampaignItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get<ApiResponse>(`${process.env.REACT_APP_API_BASE_URL}/api/campaign`);

        setData(res.data?.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse h-40 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  return (
    <section className="px-4 md:px-6 lg:px-8 py-10 bg-white">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="w-1 h-8 bg-primary2 rounded-full"></div>
          <h2 className="text-lg md:text-xl font-bold text-gray-800">Sedang Mendesak</h2>
        </div>

        <Link to={"/donasi"}>
          <button className="text-primary2 text-sm font-medium">Lihat Semua →</button>
        </Link>
      </div>

      <Swiper
        modules={[Autoplay]}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        loop={data.length > 4}
        spaceBetween={16}
        slidesPerView={1.2}
        breakpoints={{
          640: {
            slidesPerView: 3.2, // tablet → 3 + sedikit ke-4
          },
          1024: {
            slidesPerView: 4.2, // desktop → 4 + sedikit ke-5
          },
        }}
        className="!pb-2">
        {data.map((item) => (
          <SwiperSlide key={item.id}>
            <CampaignCard item={item} />
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
};

export default SedangMendesakCarousel;
